import { useCallback, useEffect, useMemo, useState } from "react"
import * as s from "./DemoHorStyle.module.scss"
import { AfterSendDemo, pixels } from "@features/Demo"
import { carrotquestId } from "@shared/external-script"
import FormFields from "./DemoComponents/DemoFormFields"
import {
  buildDemoIdentifyPayload,
  buildDemoTrackPayload,
  createIdentifyOperation,
  getDemoBlogTag,
  getDemoSubmitEvent,
  getRequestUrl,
  getYandexClientId,
  isValidName,
  trackDemoSubmitGoals,
} from "@shared/forms"

interface IDemoFormProps {
  isAudit?: boolean
  submitEvent?: string
  maxBeta?: boolean
}

export function DemoForm({ isAudit, submitEvent, maxBeta }: IDemoFormProps) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    site: "",
    role: "",
    traffic: "",
    taskDescription: "",
    platformHelp: "",
    launchDeadline: "",
  })

  const [errors, setErrors] = useState<{ [key: string]: boolean }>({})
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({})
  const [openDropdown, setOpenDropdown] = useState<string>("")
  const [phoneValid, setPhoneValid] = useState(false)
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [isNameValid, setIsNameValid] = useState(false)
  const [checked, setChecked] = useState(true)
  const [sendForm, setSendForm] = useState(false)
  const yandexClientId = getYandexClientId()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as HTMLElement).closest(".dropdown-container")) {
        setOpenDropdown("")
      }
    }
    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [])

  const validateForm = useCallback(() => {
    const newErrors: { [key: string]: boolean } = {}
    if (touched.name) {
      if (!isValidName(formData.name)) newErrors.name = true
    }
    if (phoneTouched && (!formData.phone || !phoneValid)) {
      newErrors.phone = true
    }
    ;["role", "traffic"].forEach((key) => {
      if (touched[key] && !formData[key as keyof typeof formData]) {
        newErrors[key] = true
      }
    })
    if (!formData.site) newErrors.site = true
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData, touched, phoneTouched, phoneValid])

  const isFormValid = useMemo(() => {
    return formData.name.trim() && formData.phone && formData.role && formData.traffic && validateForm()
  }, [formData, validateForm])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isFormValid) return

    const trackEvent = getDemoSubmitEvent({ isAudit, submitEvent })
    const blogTag = getDemoBlogTag()
    const pageUrl = getRequestUrl()

    const identifyPayload = buildDemoIdentifyPayload({
      ...formData,
      requestType: trackEvent,
      requestUrl: pageUrl,
      carrotId: carrotquestId(),
      yandexClientId,
    })

    if (maxBeta) {
      identifyPayload.push(createIdentifyOperation("MAX", "тестирование интеграции с MAX"))
    }

    if (blogTag) {
      identifyPayload.push(createIdentifyOperation("blog", blogTag))
    }

    carrotquest.identify(identifyPayload)

    const trackPayload = buildDemoTrackPayload({
      title: trackEvent,
      ...formData,
      requestUrl: pageUrl,
      yandexClientId,
    })

    if (maxBeta) {
      trackPayload["MAX"] = "тестирование интеграции с MAX"
    }

    if (blogTag) {
      trackPayload.blog = blogTag
    }

    carrotquest.track(trackEvent, trackPayload)

    pixels()
    setSendForm(true)
    trackDemoSubmitGoals({ isAudit })
  }

  const handleDropdownChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
    setTouched((prev) => ({ ...prev, [name]: true }))
  }

  const title = isAudit ? "Аудит от Carrot quest" : "Презентация Carrot quest"

  return (
    <div>
      {!sendForm ? (
        <div>
          <h3>{title}</h3>
          <p>
            На встрече мы расскажем как <span>Carrot quest может помочь вашему бизнесу</span>.
          </p>
          <form className={`${s.form} w-100`} onSubmit={handleSubmit}>
            <FormFields
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              touched={touched}
              setTouched={setTouched}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              phoneValid={phoneValid}
              setPhoneValid={setPhoneValid}
              setPhoneTouched={setPhoneTouched}
              isNameValid={isNameValid}
              setIsNameValid={setIsNameValid}
              checked={checked}
              setChecked={setChecked}
              isFormValid={isFormValid}
              onDropdownChange={handleDropdownChange}
            />
          </form>
        </div>
      ) : (
        <AfterSendDemo />
      )}
    </div>
  )
}
